/* eslint-disable react/forbid-prop-types */
import React, { useContext, useState } from 'react';
import { bool as boolType, any, string as stringType, number as numType } from 'prop-types';
import {
  Table,
  TableCell,
  TableRow,
  TableBody,
  Button
} from '@one-thd/sui-atomic-components';
import { Checkmark, Close } from '@one-thd/sui-icons';
import './feature-based-recommendations.scss';
import { QueryProvider, QueryContext } from '@thd-nucleus/data-sources';
import { FbrProductPod } from './FbrProductPod';

const FBRTableView = (props) => {
  const {
    data, tableData, storeId, membershipInformation, onClick, onHover, currentItemIndex,
    zipCode, itemId, recommendedFeatureSet, recommendedProductSet, scheme, isMobile
  } = props;
  const { defaultVariables } = useContext(QueryContext);
  const initialVisibleCount = 6;
  const [expanded, setExpanded] = useState(false);

  let displayProducts = data;
  const defaultVariablesComponent = {
    itemId,
    storeId,
    zipCode,
    installationServices: false,
    loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership || null,
    dataSource: 'FBR',
    ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean' && {
      isBrandPricingPolicyCompliant: defaultVariables.current.isBrandPricingPolicyCompliant
    })
  };

  return (
    <QueryProvider cacheKey="fbr-table-view-cache" defaultVariables={defaultVariablesComponent}>
      {displayProducts?.length && (
        <>
          <div className="sui-w-full sui-overflow-y-auto sui-no-scrollbar fbr-table-view">
            <Table layout="fixed" unlined>
              <TableBody>
                <TableRow key={1}>
                  {displayProducts?.map((fbrItem, idx) => {
                    const { product, recommendedFeatures } = fbrItem;
                    const { itemId: productItemId } = product;
                    const { canonicalUrl } = product?.identifiers || {};
                    const { preferredPriceFlag } = product?.pricing || {};
                    const cellProps = idx === 0 ? { sticky: 'horizontal' } : {};
                    return (
                      <TableCell
                        key={`fbr-product-pod-cell-${productItemId}`}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...cellProps}
                        className={idx === currentItemIndex ? 'current-pod' : ''}
                        width={200}
                      >
                        {idx === currentItemIndex && <div className="fbr__current-banner">current item</div>}
                        <QueryProvider cacheKey="fbr-product-pod-cache" defaultVariables={defaultVariablesComponent}>

                          <FbrProductPod
                            current={idx === currentItemIndex}
                            product={product}
                            onclick={onClick}
                            onHover={onHover}
                            fbrItem={fbrItem}
                            data={data}
                            parent="feature-based-recommendations"
                            recommendedFeatures={recommendedFeatures}
                            itemId={productItemId}
                            key={productItemId}
                            position={idx}
                            storeId={storeId}
                            scheme={scheme}
                            preferredPriceFlag={preferredPriceFlag}
                            recommendedFeatureSet={recommendedFeatureSet}
                            recommendedProductSet={recommendedProductSet}
                            anchorProduct={itemId}
                            url={canonicalUrl}
                            isMobile={isMobile}
                          />
                        </QueryProvider>

                      </TableCell>
                    );
                  })}

                </TableRow>
                {Object.keys(tableData)?.map((tableKey, index) => {
                  return (
                    <React.Fragment key={`row-main-${tableKey}`}>
                      <TableRow
                        key={`row-label-${tableKey}`}
                        className="sui-font-bold"
                        style={{
                          display: expanded || index < initialVisibleCount ? 'table-row' : 'none',
                        }}
                      >
                        {displayProducts?.map((productDetail, productIndex) => {
                          const cellProps = productIndex === 0 ? { width: 200 } : {};
                          return (
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            <TableCell key={`row-heading-${productIndex}`} {...cellProps} component="th" width={200}>
                              {productIndex === 0 ? tableKey : ''}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                      <TableRow
                        key={`row-value-${tableKey}`}
                        className="value-row"
                        style={{
                          display: expanded || index < initialVisibleCount ? 'table-row' : 'none',
                        }}
                      >
                        {displayProducts?.map((productDetail, idx) => {
                          const cellProps = idx === 0 ? { width: 200 } : {};
                          const kpfValue = tableData[tableKey]?.[productDetail?.product?.itemId] || '';
                          let renderKpfValue;
                          if (kpfValue.toLowerCase() === 'true') {
                            renderKpfValue = <Checkmark color="success" />;
                          } else if (kpfValue.toLowerCase() === 'false') {
                            renderKpfValue = <Close color="danger" />;
                          } else {
                            renderKpfValue = kpfValue;
                          }
                          return (
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            <TableCell key={`row-heading-value-${idx}`} {...cellProps} width={200}>
                              <div className="sui-line-clamp-5 sui-font-regular sui-text-base sui-leading-tight
                                sui-tracking-normal sui-normal-case sui-font-normal sui-text-primary"
                              >
                                {renderKpfValue}
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {Object.keys(tableData).length > initialVisibleCount && (
            <div className="sui-mt-2 view-more-button">
              <Button variant="text" onClick={() => setExpanded(!expanded)}>
                {expanded ? 'View Less' : 'View More'}
              </Button>
            </div>
          )}
        </>
      )}
    </QueryProvider>
  );
};

FBRTableView.displayName = 'FBRTableView';

FBRTableView.propTypes = {
  data: any,
  tableData: any,
  storeId: stringType,
  membershipInformation: any,
  onClick: any,
  onHover: any,
  currentItemIndex: numType,
  zipCode: stringType,
  itemId: stringType,
  recommendedFeatureSet: stringType,
  recommendedProductSet: stringType,
  scheme: stringType,
  isMobile: boolType
};
FBRTableView.defaultProps = {
  data: null,
  tableData: null,
  storeId: '',
  scheme: '',
  membershipInformation: null,
  onClick: () => {},
  onHover: () => {},
  currentItemIndex: 0,
  zipCode: '',
  itemId: '',
  recommendedFeatureSet: '',
  recommendedProductSet: '',
  isMobile: false
};

export {
  FBRTableView
};